import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { MessagingPort } from '../../../app/ports/messaging/messaging.port';
import { MessagingSocketService } from '../../services/messaging-socket.service';
import { MessagingSdk } from 'lib-klab-sdks';

const { CHAT_EVENTS } = MessagingSdk;

/**
 * Concrete implementation of {@link MessagingPort} backed by {@link MessagingSocketService}.
 *
 * <p>The adapter orchestrates the socket lifecycle and translates the raw
 * socket.io events into typed payloads from the AsyncAPI SDK. It contains
 * NO business rules — those belong to the consumer (UI components).
 */
@Injectable({ providedIn: 'root' })
export class MessagingPortAdapter implements MessagingPort {
  constructor(private readonly socket: MessagingSocketService) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect(): Observable<void> {
    return this.socket.connect();
  }

  disconnect(): void {
    this.socket.disconnect();
  }

  isConnected$(): Observable<boolean> {
    return this.socket.connected$;
  }

  // ── Session init ───────────────────────────────────────────────────────────

  initSession(): Observable<MessagingSdk.ChatInitResponse> {
    // Per AsyncAPI: chat:init takes no payload (user resolved from JWT).
    // Preserve the ACK object because it also carries session capabilities.
    return this.socket
      .emitWithAck<Partial<MessagingSdk.ChatInitResponse> | null>(
        CHAT_EVENTS.INIT,
        null,
      )
      .pipe(map((response) => ({
        ...(response ?? {}),
        conversations: Array.isArray(response?.conversations) ? response.conversations : [],
        canCreateGroups: response?.canCreateGroups === true,
      })));
  }

  // ── Client → Server ────────────────────────────────────────────────────────

  sendTextMessage(payload: MessagingSdk.SendTextMessagePayload): Observable<void> {
    return this.emitMessageSend(payload);
  }

  sendFileMessage(payload: MessagingSdk.SendFileMessagePayload): Observable<void> {
    return this.emitMessageSend(payload);
  }

  private emitMessageSend(payload: MessagingSdk.SendMessagePayload): Observable<void> {
    return new Observable<void>((subscriber) => {
      try {
        this.socket.emit(CHAT_EVENTS.MESSAGE_SEND, payload);
        subscriber.next();
        subscriber.complete();
      } catch (err) {
        subscriber.error(err);
      }
    });
  }

  forwardMessages(payload: MessagingSdk.ForwardMessagePayload): void {
    this.socket.emit(CHAT_EVENTS.MESSAGE_FORWARD, payload);
  }

  queryPresence(payload: MessagingSdk.PresenceQueryPayload): Observable<MessagingSdk.PresenceUpdatePayload[]> {
    return this.socket.emitWithAck<MessagingSdk.PresenceUpdatePayload[]>(
      CHAT_EVENTS.PRESENCE_QUERY,
      payload,
    );
  }

  sendPresenceHeartbeat(): void {
    this.socket.emit(CHAT_EVENTS.PRESENCE_HEARTBEAT, null);
  }

  sendTypingStart(payload: MessagingSdk.TypingPayload): void {
    this.socket.emit(CHAT_EVENTS.TYPING_START, payload);
  }

  sendTypingStop(payload: MessagingSdk.TypingPayload): void {
    this.socket.emit(CHAT_EVENTS.TYPING_STOP, payload);
  }

  acknowledgeDelivery(payload: MessagingSdk.MessageDeliveredPayload): void {
    this.socket.emit(CHAT_EVENTS.MESSAGE_DELIVERED, payload);
  }

  markAsRead(payload: MessagingSdk.MessageReadPayload): Observable<MessagingSdk.MessageReadAck> {
    return this.socket.emitWithAck<MessagingSdk.MessageReadAck>(CHAT_EVENTS.MESSAGE_READ, payload);
  }

  createDirectConversation(payload: MessagingSdk.CreateDirectConversationPayload): void {
    this.socket.emit(CHAT_EVENTS.CONVERSATION_CREATE, payload);
  }

  createGroupConversation(payload: MessagingSdk.CreateGroupConversationPayload): void {
    this.socket.emit(CHAT_EVENTS.CONVERSATION_CREATE, payload);
  }

  addGroupMembers(payload: MessagingSdk.AddGroupMembersPayload): void {
    this.socket.emit(CHAT_EVENTS.GROUP_MEMBER_ADD, payload);
  }

  removeGroupMember(payload: MessagingSdk.RemoveGroupMemberPayload): void {
    this.socket.emit(CHAT_EVENTS.GROUP_MEMBER_REMOVE, payload);
  }

  updateGroupMemberRole(payload: MessagingSdk.UpdateGroupMemberRolePayload): void {
    this.socket.emit(CHAT_EVENTS.GROUP_MEMBER_ROLE_UPDATE, payload);
  }

  fetchMessages(payload: MessagingSdk.MessagesFetchPayload): Observable<MessagingSdk.MessagesFetchedResponse> {
    return this.socket.emitWithAck<MessagingSdk.MessagesFetchedResponse>(
      CHAT_EVENTS.MESSAGES_FETCH,
      payload,
    ).pipe(map((response) => {
      if (!response.success) {
        throw new Error(`messages_fetch_${response.errorCode ?? 'failed'}`);
      }
      return response;
    }));
  }

  fetchMessageSeenBy(conversationId: string, messageId: string): Observable<MessagingSdk.MessageSeenByFetchedResponse> {
    const payload: MessagingSdk.MessageSeenByFetchPayload = { conversationId, messageId };
    return this.socket.emitWithAck<MessagingSdk.MessageSeenByFetchedResponse>(
      CHAT_EVENTS.MESSAGE_SEEN_BY_FETCH,
      payload,
    );
  }

  fetchConversationFiles(payload: MessagingSdk.ChatFilesFetchPayload): Observable<MessagingSdk.ChatFilesFetchedResponse> {
    return this.socket.emitWithAck<MessagingSdk.ChatFilesFetchedResponse>(
      CHAT_EVENTS.FILES_FETCH,
      payload,
    );
  }

  fetchMessagesAround(payload: MessagingSdk.MessagesAroundPayload): Observable<MessagingSdk.MessagesAroundResponse> {
    return this.socket.emitWithAck<MessagingSdk.MessagesAroundResponse>(
      CHAT_EVENTS.MESSAGES_AROUND,
      payload,
    );
  }

  jumpToDate(payload: MessagingSdk.MessagesDateJumpPayload): Observable<MessagingSdk.MessagesAroundResponse> {
    return this.socket.emitWithAck<MessagingSdk.MessagesAroundResponse>(
      CHAT_EVENTS.MESSAGES_DATE_JUMP,
      payload,
    );
  }

  searchMessages(payload: MessagingSdk.MessagesSearchPayload): Observable<MessagingSdk.MessagesSearchResponse> {
    return this.socket.emitWithAck<MessagingSdk.MessagesSearchResponse>(
      CHAT_EVENTS.MESSAGES_SEARCH,
      payload,
    );
  }

  deleteGroup(payload: MessagingSdk.DeleteGroupPayload): void {
    this.socket.emit(CHAT_EVENTS.GROUP_DELETE, payload);
  }

  updateGroup(payload: MessagingSdk.UpdateGroupPayload): void {
    this.socket.emit(CHAT_EVENTS.GROUP_UPDATE, payload);
  }

  editMessage(payload: MessagingSdk.EditMessagePayload): void {
    this.socket.emit(CHAT_EVENTS.MESSAGE_EDIT, payload);
  }

  deleteMessage(payload: MessagingSdk.DeleteMessagePayload): void {
    this.socket.emit(CHAT_EVENTS.MESSAGE_DELETE, payload);
  }

  pinMessage(payload: MessagingSdk.PinMessagePayload): void {
    this.socket.emit(CHAT_EVENTS.MESSAGE_PIN, payload);
  }

  toggleMessageReaction(payload: MessagingSdk.ToggleMessageReactionPayload): Observable<MessagingSdk.MessageReactionAck> {
    return this.socket.emitWithAck<MessagingSdk.MessageReactionAck>(
      CHAT_EVENTS.MESSAGE_REACTION_TOGGLE,
      payload,
    );
  }

  pinConversation(payload: MessagingSdk.PinConversationPayload): void {
    this.socket.emit(CHAT_EVENTS.CONVERSATION_PIN, payload);
  }

  // ── Server → Client streams ────────────────────────────────────────────────

  onNewMessage$(): Observable<MessagingSdk.NewMessagePayload> {
    return this.socket.onMessageNew<MessagingSdk.NewMessagePayload>();
  }

  onMediaRejected$(): Observable<MessagingSdk.MediaMutationRejectedPayload> {
    return this.socket.on<MessagingSdk.MediaMutationRejectedPayload>(CHAT_EVENTS.MEDIA_REJECTED);
  }

  onConversationCreated$(): Observable<MessagingSdk.ConversationCreatedPayload> {
    return this.socket.onConversationCreated<MessagingSdk.ConversationCreatedPayload>();
  }

  onConversationNew$(): Observable<MessagingSdk.ConversationCreatedPayload> {
    return this.socket.onConversationNew<MessagingSdk.ConversationCreatedPayload>();
  }

  onMessageDeliveredUpdate$(): Observable<MessagingSdk.MessageDeliveredUpdatePayload> {
    return this.socket.onMessageDeliveredUpdate<MessagingSdk.MessageDeliveredUpdatePayload>();
  }

  onMessageReadUpdate$(): Observable<MessagingSdk.MessageReadUpdatePayload> {
    return this.socket.onMessageReadUpdate<MessagingSdk.MessageReadUpdatePayload>();
  }

  onPresenceUpdate$(): Observable<MessagingSdk.PresenceUpdatePayload> {
    return this.socket.onPresenceUpdate<MessagingSdk.PresenceUpdatePayload>();
  }

  onTypingIndicator$(): Observable<MessagingSdk.TypingIndicatorPayload> {
    return this.socket.onTypingIndicator<MessagingSdk.TypingIndicatorPayload>();
  }

  onGroupMemberAdded$(): Observable<MessagingSdk.GroupMemberAddedPayload> {
    // CHAT_EVENTS.GROUP_MEMBER_ADDED = 'chat:group:member:added'.
    return this.socket.on<MessagingSdk.GroupMemberAddedPayload>(CHAT_EVENTS.GROUP_MEMBER_ADDED);
  }

  onGroupUpdated$(): Observable<MessagingSdk.GroupUpdatedPayload> {
    return this.socket.on<MessagingSdk.GroupUpdatedPayload>(CHAT_EVENTS.GROUP_UPDATED);
  }

  onMessageForwardBlocked$(): Observable<MessagingSdk.MessageForwardBlockedPayload> {
    return this.socket.on<MessagingSdk.MessageForwardBlockedPayload>(CHAT_EVENTS.MESSAGE_FORWARD_BLOCKED);
  }

  onMessageReadResult$(): Observable<MessagingSdk.MessageReadResultPayload> {
    return this.socket.on<MessagingSdk.MessageReadResultPayload>(CHAT_EVENTS.MESSAGE_READ_RESULT);
  }

  onGroupMemberRemoved$(): Observable<MessagingSdk.GroupMemberRemovedPayload> {
    return this.socket.on<MessagingSdk.GroupMemberRemovedPayload>(CHAT_EVENTS.GROUP_MEMBER_REMOVED);
  }

  onGroupMemberRoleUpdated$(): Observable<MessagingSdk.GroupMemberRoleUpdatedPayload> {
    return this.socket.on<MessagingSdk.GroupMemberRoleUpdatedPayload>(CHAT_EVENTS.GROUP_MEMBER_ROLE_UPDATED);
  }

  onGroupMemberRoleUpdateResult$(): Observable<MessagingSdk.GroupMemberRoleUpdateResultPayload> {
    return this.socket.on<MessagingSdk.GroupMemberRoleUpdateResultPayload>(CHAT_EVENTS.GROUP_MEMBER_ROLE_UPDATE_RESULT);
  }

  onGroupDeleted$(): Observable<MessagingSdk.GroupDeletedPayload> {
    return this.socket.on<MessagingSdk.GroupDeletedPayload>(CHAT_EVENTS.GROUP_DELETED);
  }

  onParticipantIdentityUpdated$(): Observable<MessagingSdk.ParticipantIdentityUpdatedPayload> {
    return this.socket.on<MessagingSdk.ParticipantIdentityUpdatedPayload>(CHAT_EVENTS.PARTICIPANT_IDENTITY_UPDATED);
  }

  onMessageEdited$(): Observable<MessagingSdk.MessageEditedPayload> {
    return this.socket.on<MessagingSdk.MessageEditedPayload>(CHAT_EVENTS.MESSAGE_EDITED);
  }

  onMessageDeleted$(): Observable<MessagingSdk.MessageDeletedPayload> {
    return this.socket.on<MessagingSdk.MessageDeletedPayload>(CHAT_EVENTS.MESSAGE_DELETED);
  }

  onMessagePinned$(): Observable<MessagingSdk.MessagePinnedPayload> {
    return this.socket.on<MessagingSdk.MessagePinnedPayload>(CHAT_EVENTS.MESSAGE_PINNED);
  }

  onMessageReactionsChanged$(): Observable<MessagingSdk.MessageReactionsChangedPayload> {
    return this.socket.on<MessagingSdk.MessageReactionsChangedPayload>(CHAT_EVENTS.MESSAGE_REACTIONS_CHANGED);
  }

  onConversationPinned$(): Observable<MessagingSdk.ConversationPinnedPayload> {
    return this.socket.on<MessagingSdk.ConversationPinnedPayload>(CHAT_EVENTS.CONVERSATION_PINNED);
  }

  onError$(): Observable<MessagingSdk.ErrorPayload> {
    return this.socket.onError<MessagingSdk.ErrorPayload>();
  }
}
